import {
  FieldAlert,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { ApiErrorStatusCode, isResponseWithData } from 'lib-shared-auth';
import { Satisfaction } from 'lib-shared-legacy-types';
import noop from 'lodash/noop';
import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { ratingApi } from 'apis/rating';
import { HookFormSelectionListSingle, HookFormText } from 'components/hookForm';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './surveyAddModal.module.scss';

export type FieldValues = {
  surveyType: Satisfaction.SurveyType;
  surveyName: string;
};

/** A component that displays add survey modal. */
export const SurveyAddModal = createAppModal(
  { type: 'settings:addSurvey' },
  ({ isOpen, hide }): JSX.Element => {
    const flags = useFeatureFlag(['csat-for-phone-via-sms']);

    const navigate = useAppNavigate();

    /** Temporary until Csat4phone via sms is GA. */
    const surveyTypes: Satisfaction.SurveyType[] = flags.csatForPhoneViaSms
      ? Satisfaction.surveyStyles
      : ['text_to_speech'];

    const [error, setError] = useState('');

    const { formState, control, handleSubmit, resetField } =
      useForm<FieldValues>({
        defaultValues: {
          surveyType: surveyTypes[0],
          surveyName: '',
        },
        mode: 'all',
      });

    const surveyType = useWatch({
      control,
      name: 'surveyType',
    });

    useEffect(() => {
      resetField('surveyName');
      setError('');
    }, [surveyType]);

    const handleCreateRatingSettings = useCallback(
      async (fieldValues: FieldValues) => {
        try {
          const createResponse = await createRatingSettings({
            level: 'survey',
            name: fieldValues.surveyName,
            survey_type: fieldValues.surveyType,
          }).unwrap();

          if (isResponseWithData(createResponse)) {
            handleClose();
            handleGoToSurvey(createResponse.data.id);
          }
        } catch (e) {
          const error = e as unknown as {
            status: number;
            data: {
              errors: string[];
            };
          };

          setError(
            error && error.status === ApiErrorStatusCode.Conflict
              ? 'Survey name already exists.'
              : 'Something went wrong.',
          );
        }
      },
      [navigate],
    );

    /** Handles closing the modal. */
    const handleClose = () => {
      resetField('surveyName');
      hide?.();
    };

    const handleGoToSurvey = useCallback(
      (settingsId: Satisfaction.CsatSettings['id']) => {
        navigate({
          path: '/settings/satisfaction/phone/survey/:settingsId',
          params: { path: { settingsId } },
        });
      },
      [navigate],
    );

    const [createRatingSettings] = ratingApi.useLazyCreateRatingSettingsQuery();

    const handleValidSubmit: SubmitHandler<FieldValues> = async (
      fieldValues,
    ) => {
      await handleCreateRatingSettings(fieldValues);
    };

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <form
          noValidate
          tabIndex={-1}
          onSubmit={handleSubmit(handleValidSubmit)}
        >
          <ModalHead
            title={
              <FlexBox gap="200">
                <Text.Heading>Add survey</Text.Heading>
              </FlexBox>
            }
            onClose={handleClose}
          />

          <ModalBody>
            <FlexBox flexDirection="column" gap="400">
              <FlexBox flexDirection="column">
                <HookFormSelectionListSingle
                  name="surveyType"
                  label="Survey send method"
                  control={control}
                  componentProps={{
                    getItemTitleByValue: (
                      value: Satisfaction.SurveyType | undefined | null,
                    ) => (value ? Satisfaction.prettySurveyType(value) : ''),
                  }}
                >
                  {surveyTypes.map((surveyType) => (
                    <SelectionList.Item key={surveyType} value={surveyType}>
                      <Text.Paragraph>
                        {Satisfaction.prettySurveyType(surveyType)}
                      </Text.Paragraph>
                    </SelectionList.Item>
                  ))}
                </HookFormSelectionListSingle>

                <HookFormText
                  name="surveyName"
                  label="CSAT survey name"
                  control={control}
                  noAlert={true}
                  componentProps={{
                    placeholder: 'CSAT survey name',
                  }}
                  rules={{
                    required: 'CSAT survey name is required',
                  }}
                ></HookFormText>
              </FlexBox>
              <FlexBox flexDirection="column">
                {error && <FieldAlert variant="error">{error}</FieldAlert>}
              </FlexBox>
            </FlexBox>
          </ModalBody>

          <ModalFoot
            cancelOnClick={handleClose}
            confirmName="Add"
            confirmOnClick={noop} // A hack to show confirm button
            confirmProps={{
              disabled: !(formState.isValid && formState.isDirty),
              type: 'submit',
              isLoading: formState.isSubmitting,
            }}
          />
        </form>
      </Modal>
    );
  },
);
